import * as WebViewBridge from '@/checkout-instore-legacy/utils/WebViewBridge'
import {
  getDefaultProductImages,
  getSkuById,
  getSkuPrice,
  isSkuAvailable,
} from '@/checkout-instore-legacy/utils/CatalogUtils'
import { safeParseInt } from '@/checkout-instore-legacy/utils/numericUtils'
import compact from 'lodash/compact'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import isNumber from 'lodash/isNumber'
import {
  ProductPageStateFromProductStore,
  ProductStore,
  SkuSelectorProps,
  SkuStock,
} from './typings'
import {
  getDefaultSeller,
  getSellerAvailableQuantity,
} from '@/checkout-instore-legacy/seller/utils'
import {
  getSimulationLogisticsInfo,
  hasSkusSimulatonData,
} from '@/checkout-instore-legacy/cart/simulation/utils'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'

const { showSelectorForSingleSku } = getInstoreConfig()

export const getStateFromProductStoreChange = (
  productId: string,
  skuId: string,
  productStore: ProductStore,
  intl: IntlObject | null = null
): ProductPageStateFromProductStore => {
  const result: ProductPageStateFromProductStore = {
    updatingProduct: false,
  }

  WebViewBridge.sendEvent({
    type: 'finishRefreshingPullToRefresh',
  })

  const productData = get(productStore, ['products', productId])

  if (productData) {
    result.error = get(productData, 'error')
    result.errorWithCacheDisabled = get(productData, 'errorWithCacheDisabled')
    result.product = get(productData, 'data')

    if (result.product) {
      result.defaultProductImages = getDefaultProductImages(result.product)

      let sku = null
      if (!isNil(skuId)) {
        const cachedSku = get(productStore, ['skuSimulationCache', skuId])
        sku = cachedSku || getSkuById(get(result.product, 'skus'), skuId)
      }

      if (sku != null) {
        if (intl) {
          result.selectedVariations = getVariationsFromSku(
            sku,
            get(result.product, 'name'),
            intl
          )
        }
        result.sku = sku
      }
    }
  }

  return result
}

export const getRouteParameters = (
  location: any,
  routeParams: any
): {
  quantity: number
  manualPrice: number | null
  skuId: string
  productId: string
  index: string
  previousPage: string | null
} => {
  const { skuId, manualPrice, index, quantity, previousPage } = location.query
  const productId = routeParams.productId

  return {
    quantity: safeParseInt(quantity) || 1,
    manualPrice: safeParseInt(manualPrice),
    skuId,
    productId,
    index,
    previousPage,
  }
}

export const removeProductNameFromSku = (
  skuName: string,
  productName?: string
) => {
  /* Regex to remove product name and separators from the displayed sku */
  const productNameRegExp = new RegExp(
    productName ? `^(${productName}( |-|\\/|\\\\|:|=)*)` : '.^'
  )

  return skuName && skuName.replace(productNameRegExp, '')
}

export const getVariationsFromSku = (
  sku: Item,
  productName = '',
  intl: IntlObject
) => {
  const selectedVariations: Record<string, any> = {}
  const variations: Record<string, any>[] | undefined = sku.variations

  if (variations?.length) {
    variations.forEach(
      (variation) => (selectedVariations[variation.label] = variation.value)
    )
  } else {
    const genericVariationLabel = intl.formatMessage({
      id: 'variationLabel',
    })
    const variationName = removeProductNameFromSku(sku.name, productName)
    selectedVariations[genericVariationLabel] = variationName
  }

  return selectedVariations
}

export const getSkuSelectorProps = ({
  product,
  logisticsInfoCache,
  intl,
}: {
  product: Product
  logisticsInfoCache: Record<string, LogisticsInfo>
  intl: IntlObject
}): SkuSelectorProps => {
  const { skus, variations, name: productName } = product

  const props: SkuSelectorProps = { variations: [], skus: [] }

  if (isEmpty(skus)) {
    return props
  }

  const variationsRaw = variations?.map((variation: Record<string, any>) => ({
    name: variation.label,
    hasThumbs: false,
  }))

  props.variations = variationsRaw || []

  /** skuVariation is true when there are no variations defined and multiple skus,
   *  so that each sku will define the variation */
  const genericVariationLabel = intl.formatMessage({ id: 'variationLabel' })
  let skuVariation = false
  if (props.variations.length === 0) {
    skuVariation = true
    props.variations = [{ name: genericVariationLabel, hasThumbs: false }]
  }

  const initialQuantity = 1

  const nonNilSkus = compact(skus)
  const skusRaw = nonNilSkus.map((sku) => {
    let variationsOption: Record<string, string> = {}

    if (skuVariation) {
      variationsOption[genericVariationLabel] = removeProductNameFromSku(
        sku.name,
        productName
      )
    } else {
      variationsOption = getVariationsFromSku(sku, productName, intl)
    }

    const logisticsInfo = getSimulationLogisticsInfo(
      logisticsInfoCache,
      sku.id,
      initialQuantity
    )
    const available = isSkuAvailable(sku, logisticsInfo)
    const seller = getDefaultSeller(sku.sellers)
    const price = available
      ? {
          value: seller.commertialOffer.price,
          notUnique: false,
        }
      : undefined // price can't be false

    return {
      price,
      sku,
      available,
      videos: sku.videos,
      ...variationsOption,
    }
  })
  props.skus = skusRaw || []
  return props
}

export const getProductSkuData = (
  product: Product | undefined,
  sku: Item | undefined,
  defaultProductImages: string[],
  allVariationsSelected: boolean | undefined,
  logisticsInfoCache: Record<string, LogisticsInfo>
) => {
  const initialQuantity = 1

  const productDescription = get(product, 'description')
  const skus = (product && get(product, 'skus')) || []
  const skuImages = get(sku, 'images', defaultProductImages)
  const skuVideos = get(sku, 'videos', [])
  const availableQuantity =
    sku && getSellerAvailableQuantity(get(sku, 'sellers'))

  const logisticsInfo: LogisticsInfo = getSimulationLogisticsInfo(
    logisticsInfoCache,
    sku && sku.id,
    initialQuantity
  )

  const skuIsAvailableAtInitialQuantity: boolean = !isEmpty(logisticsInfo)
    ? !!sku && isSkuAvailable(sku, logisticsInfo)
    : !!sku && isNumber(getSkuPrice(sku))

  const hasSimulationData = hasSkusSimulatonData(
    logisticsInfoCache,
    skus,
    initialQuantity
  )

  const isAllVariationsSelected =
    allVariationsSelected || (skus && skus.length === 1) || false

  const showSkuSelector =
    skus?.length > 1 ||
    (showSelectorForSingleSku && !isEmpty(skus[0]?.variations))

  return {
    availableQuantity,
    productDescription,
    hasSimulationData,
    showSkuSelector,
    skuImages,
    skuVideos,
    skuIsAvailableAtInitialQuantity,
    isAllVariationsSelected,
  }
}

export const getSkuStock = (
  sku: Item | undefined,
  stock: Record<string, SkuStock> | undefined,
  availableQuantity: number | undefined,
  isLoadingStock: boolean | undefined
): SkuStock => {
  const zeroStock = {
    checkedInStock: 0,
    otherStock: 0,
  }
  const useZeroStock = isLoadingStock && availableQuantity === 0
  const skuStock = (stock && sku && stock[sku?.id]) || zeroStock

  return useZeroStock ? zeroStock : skuStock
}

export const getSpecifications = (
  sku: Item | undefined,
  product: Product | undefined,
  isAllVariationsSelected: boolean,
  intl: IntlObject
) => {
  let specifications: { name: string; values: string[] }[] = []

  if (sku && isAllVariationsSelected) {
    const ean = get(sku, 'ean')
    const refId = get(sku, 'refId')

    if (ean) {
      specifications.push({
        name: intl.formatMessage({ id: 'productDataBarcode' }),
        values: [ean],
      })
    }

    if (refId) {
      specifications.push({
        name: intl.formatMessage({ id: 'productDataCodRef' }),
        values: [refId],
      })
    }
  }

  if (product) {
    const brand = get(product, 'brand')
    const productSpecifications: { label: string; options: string }[] = get(
      product,
      'allSpecifications',
      []
    )

    brand &&
      specifications.push({
        name: intl.formatMessage({ id: 'productDataBrand' }),
        values: [brand],
      })

    specifications = specifications.concat(
      productSpecifications.map((specification) => ({
        name: specification.label,
        values: [specification.options],
      }))
    )
  }
  return specifications
}
